import {
  faBullhorn,
  faChartPie,
  faCog,
  faHandsHelping,
  faHeadset,
  faLock,
  faQuestionCircle,
  faReplyAll,
  faSlidersH,
  faStore,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useRef, useState } from 'react';
import { usePermission } from '../../../../../../hooks/usePermission';
import { Booth } from '../../../../../../interfaces/interfaces';
import { useSelector } from '../../../../../../reduxToolkit/typedRedux';
import AnalyticsTab from './BigBoothEdit/AnalyticsTab';
import BoothAutoReplyTab from './BigBoothEdit/BoothAutoReplyTab';
import BoothConfigTab from './BigBoothEdit/BoothConfigTab';
import BoothFiltersTab from './BigBoothEdit/BoothFiltersTab';
import BoothInformationTab from './BigBoothEdit/BoothInformationTab';
import BoothMeetingPartnerTab from './BigBoothEdit/BoothMeetingPartnerTab';
import BoothNetworkingTab from './BigBoothEdit/BoothNetworkingTab';
import BoothTab from './BigBoothEdit/BoothTab';
import BoothUserPrivileges from './BigBoothEdit/BoothUserPrivilegesTab';
import LeadsTab from './BigBoothEdit/LeadsTab';
import { Tab, Tabs } from '@mui/material';
import BoothProgress from './BoothProgress';
import { useDispatch } from 'react-redux';
import { setCurrentBoothTab } from '../../../../../../reduxToolkit/slices/adminSlice';

interface BigBoothInterface {
  booth: Booth;
  updateHeight?: () => void;
  isModal?: boolean;
}

const BigBooth = ({ booth, updateHeight, isModal }: BigBoothInterface) => {
  const user = useSelector((state) => state.common.user);
  const event = useSelector((state) => state.common.event);
  const advancedFilters = useSelector((state) => state.common.advancedFilters);
  const currentBoothTab = useSelector((state) => state.admin.currentBoothTab);

  const { t } = useTranslation();
  const { isEventHost, isBoothHost } = usePermission();
  const dispatch = useDispatch();

  const [height, setHeight] = useState<number>();
  const tabsRef = useRef<HTMLDivElement | null>(null);

  const tabs: { id: string; title: string; hidden?: boolean; icon: JSX.Element; onClick?: () => void; component: JSX.Element }[] = [
    {
      id: 'booth',
      title: t('pagebuilder:booth'),
      component: <BoothTab booth={booth} setHeight={setHeight} />,
      icon: <FontAwesomeIcon icon={faStore} />,
    },
    {
      id: 'leads',
      title: t('pagebuilder:leads'),
      component: <LeadsTab booth={booth} />,
      icon: <FontAwesomeIcon icon={faUsers} />,
    },
    {
      id: 'analytics',
      title: t('pagebuilder:analytics'),
      hidden: !(event?.settings?.showBoothStats && booth?.settings?.showBoothStats?.value),
      component: <AnalyticsTab booth={booth} />,
      icon: <FontAwesomeIcon icon={faChartPie} />,
    },
    {
      id: 'exhibitorinfo',
      title: t('account:exhibitorinformation-exhibitor'),
      component: <BoothInformationTab booth={booth} />,
      icon: <FontAwesomeIcon icon={faBullhorn} />,
    },
    {
      id: 'networking',
      title: t('account:networking'),
      hidden: booth?.networkingrooms?.length === 0 || !booth?.settings?.displayNetworkingroomsInBooth?.value,
      component: <BoothNetworkingTab booth={booth} />,
      icon: <FontAwesomeIcon icon={faHandsHelping} />,
    },
    {
      id: 'filters',
      title: t('account:filters'),
      hidden: !(
        (event?.modules?.advancedFilters?.active === true && event?.modules.advancedFilters.settings.allowSelfSelectValues) ||
        (isEventHost() && advancedFilters.length > 0)
      ),
      component: <BoothFiltersTab booth={booth} />,
      icon: <FontAwesomeIcon icon={faSlidersH} />,
    },
    {
      id: 'autoreply',
      title: t('account:autoreply'),
      hidden: !booth?.settings?.displayContactShareInBooth?.value,
      component: <BoothAutoReplyTab booth={booth} />,
      icon: <FontAwesomeIcon icon={faReplyAll} />,
    },
    {
      id: 'configuration',
      title: t('account:app-configuration'),
      component: <BoothConfigTab booth={booth} />,
      icon: <FontAwesomeIcon icon={faCog} />,
    },
    {
      id: 'privilege',
      title: t('account:user-privilege'),
      component: <BoothUserPrivileges booth={booth} />,
      icon: <FontAwesomeIcon icon={faLock} />,
    },
    {
      id: 'meetingpartner',
      title: t('account:meetingpartner'),
      hidden: !event?.settings?.showBoothPotentialQueueUsers,
      component: <BoothMeetingPartnerTab booth={booth} />,
      icon: <FontAwesomeIcon icon={faHeadset} />,
    },
    {
      id: 'faq',
      title: t('account:faq-aussteller'),
      onClick: () => window.open('https://vystem.notion.site/Aussteller-532d39c69ea24f07b64db9383a7d28b6', '_blank'),
      component: <></>,
      icon: <FontAwesomeIcon icon={faQuestionCircle} />,
    },
  ];

  useEffect(() => updateHeight?.(), [height]);

  return (
    <div className="w-full max-w-full h-full max-h-full">
      {(isEventHost(user) || isBoothHost(user, [booth])) && (
        <>
          <div ref={tabsRef}>
            <Tabs allowScrollButtonsMobile variant="scrollable" value={currentBoothTab} indicatorColor="primary" textColor="primary">
              {tabs
                .filter((tab) => !tab.hidden)
                .map((_t) => (
                  <Tab
                    icon={_t.icon}
                    iconPosition="start"
                    label={_t.title}
                    onClick={() => (_t.onClick ? _t.onClick() : dispatch(setCurrentBoothTab(_t.id)))}
                    value={_t.id}
                  />
                ))}
            </Tabs>
          </div>
          <BoothProgress boothid={booth._id} />
        </>
      )}
      <div className="overflow-auto" style={{ height: isModal ? '75vh' : 'auto', minHeight: isModal ? undefined : '75vh' }}>
        {tabs.filter((tab) => !tab.hidden).find((t) => t.id === currentBoothTab)?.component ?? <></>}
      </div>
    </div>
  );
};
export default BigBooth;
